import type { TeammateId } from "@repo/app-types/entities/teammate";
import { isNotUndefined } from "@repo/app-types/helpers/utilities";
import { fetchChatCompletion } from "@repo/ui/chat/api";
import { MessageItem } from "@repo/ui/chat/messageItem";
import { MessageTimeline } from "@repo/ui/chat/messageTimeline";
import type {
  ClientChatMessage,
  ClientUserChatMessage,
} from "@repo/ui/chat/types";
import { UserMessageForm } from "@repo/ui/chat/userMessageForm";
import * as Sentry from "@sentry/react";
import { type FC, useEffect, useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { useLazyTeammateCopilotMessagesQuery } from "../../api/entities/teammate.js";
import {
  apiBaseUrl,
  getApiAuthorizationHeaderValue,
} from "../../api/shared.js";
import { useAppSelector } from "../../hooks/hook.js";
import { selectTeammate } from "../teammate/teammateSlice.js";

interface TeammateAgentSimulationCopilotProps {
  teammateAgentId: TeammateId;
  shouldUseLiveToolCalls: boolean;
}

interface BaseCompletionRequest {
  type: string;
}

interface InitialCompletionRequest extends BaseCompletionRequest {
  type: "initial";
}

interface UserMessageCompletionRequest extends BaseCompletionRequest {
  type: "user_message";
  message: ClientUserChatMessage;
}

type CompletionRequest =
  | InitialCompletionRequest
  | UserMessageCompletionRequest;

export const TeammateAgentSimulationCopilot: FC<
  TeammateAgentSimulationCopilotProps
> = (props) => {
  const { teammateAgentId, shouldUseLiveToolCalls } = props;
  const currentTeammate = useAppSelector(selectTeammate);

  const [messages, setMessages] = useState<readonly ClientChatMessage[]>([]);

  const [completionRequests, setCompletionRequests] = useState<
    readonly CompletionRequest[]
  >([]);

  // Lazy fetch current messages for this teammate chat
  const [fetchTeammateCopilotMessages] = useLazyTeammateCopilotMessagesQuery();

  const currentTeammateId = currentTeammate?.id;

  // Populate messages when "teammateCopilotMessages" is available
  useEffect(() => {
    const fetchMessages = async () => {
      if (currentTeammateId) {
        try {
          const teammateChatMessages = await fetchTeammateCopilotMessages({
            teammateId: currentTeammateId,
            chatDetails: {
              type: "teammate_agent",
              teammate_agent_id: teammateAgentId,
            },
          }).unwrap();

          // Reset the list of messages with the data coming from the server.
          setMessages(teammateChatMessages);

          // If there are no messages, trigger an initial AI message
          if (teammateChatMessages.length === 0) {
            setCompletionRequests([{ type: "initial" }]);
          }
        } catch (error) {
          console.error("Failed to fetch teammate copilot messages", error);
        }
      }
    };

    fetchMessages();
  }, [fetchTeammateCopilotMessages, currentTeammateId, teammateAgentId]);

  const handleMessageSend = (message: string) => {
    // Enqueue the user message for chat completion
    setCompletionRequests((prev) => [
      ...prev,
      {
        type: "user_message",
        message: {
          id: uuidv4(),
          role: "user",
          createdAt: new Date().toISOString(),
          content: message,
          isSaving: true,
        },
      },
    ]);
  };

  const handleMessageUpdate = (message: ClientChatMessage) => {
    // Upsert the message
    setMessages((prev) => {
      const index = prev.findIndex((m) => m.id === message.id);
      if (index === -1) {
        return [...prev, message];
      }

      const updatedMessages = [...prev];
      updatedMessages[index] = message;
      return updatedMessages;
    });
  };

  const handleMessageRetry = (messageId: string) => {
    // Find the message
    const message = messages.find((m) => m.id === messageId);
    if (!message) {
      return;
    }

    // Remove it from the list
    setMessages((prev) => prev.filter((m) => m.id !== messageId));

    // Requeue a completion request
    switch (message.role) {
      case "user":
        setCompletionRequests((prev) => [
          ...prev,
          {
            type: "user_message",
            message: {
              ...message,
              isSaving: true,
              isError: false,
            },
          },
        ]);
        break;
    }
  };

  // Handle chat completion requests
  const currentCompletionRequest = completionRequests.at(0);
  useEffect(() => {
    if (!currentTeammate || !currentCompletionRequest) {
      return;
    }

    const userMessage =
      currentCompletionRequest.type === "user_message"
        ? currentCompletionRequest.message
        : null;

    const performCompletion = async (signal: AbortSignal) => {
      const authorizationHeader = await getApiAuthorizationHeaderValue();

      const url = new URL(
        `/teammates/${currentTeammate?.id}/copilot`,
        apiBaseUrl,
      );

      const updatedMessages = fetchChatCompletion(url, {
        headers: {
          Authorization: authorizationHeader,
          "x-alpharun-commit-sha": __COMMIT_SHA__,
        },
        body: {
          // We proactively generate an assistant message when starting/resuming the lesson
          user_chat_message: userMessage
            ? {
                id: userMessage.id,
                content: userMessage.content,
              }
            : null,
          chat_details: {
            type: "teammate_agent",
            teammate_agent_id: teammateAgentId,
          },
          use_live_tool_calls: shouldUseLiveToolCalls,
        },
        signal,
      });

      for await (const message of updatedMessages) {
        // Once we receive an assistant message, make the user message permanent
        if (userMessage) {
          handleMessageUpdate({
            ...userMessage,
            isSaving: false,
          });
        }

        handleMessageUpdate(message);
      }
    };

    const abortController = new AbortController();
    performCompletion(abortController.signal).then(
      () => {
        // Dequeue the completion request
        setCompletionRequests((prev) =>
          prev.filter((req) => req !== currentCompletionRequest),
        );
      },
      (error) => {
        // If the effect was canceled, keep the completion request for next render (mostly happens in React strict mode)
        if (error.name === "AbortError") {
          return;
        }

        // Add an error message to the list
        if (userMessage) {
          handleMessageUpdate({
            ...userMessage,
            isSaving: false,
            isError: true,
          });
        }

        // Dequeue the completion request
        setCompletionRequests((prev) =>
          prev.filter((req) => req !== currentCompletionRequest),
        );

        Sentry.captureException(error);
      },
    );

    return () => {
      abortController.abort();
    };
  }, [
    currentTeammate,
    currentCompletionRequest,
    teammateAgentId,
    shouldUseLiveToolCalls,
  ]);

  const visibleMessages = useMemo(() => {
    // Filter out empty messages (but keep tool messages even if content is minimal)
    const nonEmptyMessages = messages.filter(
      (message) => message.role === "tool" || !!message.content,
    );

    // Show user messages that are still in the queue
    const pendingUserMessages = completionRequests
      .map((req): ClientUserChatMessage | undefined =>
        req.type === "user_message" &&
        !messages.some((m) => m.id === req.message.id)
          ? req.message
          : undefined,
      )
      .filter(isNotUndefined);

    return [...nonEmptyMessages, ...pendingUserMessages];
  }, [messages, completionRequests]);

  // Show a typing indicator until we have a partial assistant message
  const lastMessage = messages.at(-1);
  const isLastMessageLoading =
    lastMessage?.role === "assistant" &&
    lastMessage.isLoading &&
    lastMessage.content;
  const isLoading = completionRequests.length > 0 && !isLastMessageLoading;

  return (
    <div className="-m-4 flex flex-auto flex-col overflow-hidden">
      <MessageTimeline messages={visibleMessages} isLoading={isLoading}>
        {(message) => (
          <MessageItem
            role={message.role}
            isSaving={message.role === "user" && message.isSaving}
            isError={message.isError}
            onRetry={() => handleMessageRetry(message.id)}
          >
            {message.role === "tool" ? (
              <div>
                <div className="mb-1 text-xs font-semibold text-amber-700">
                  Tool Call
                </div>
                {message.alternativeContent || message.content}
              </div>
            ) : (
              message.content
            )}
          </MessageItem>
        )}
      </MessageTimeline>

      <div className="p-4">
        <UserMessageForm
          isDisabled={isLoading}
          onMessageSend={handleMessageSend}
        />
      </div>
    </div>
  );
};
